import React, { useEffect, useRef, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { Order } from "square";
import { getOrder } from "../api";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { useSelectedLocation } from "../contexts/SelectedLocationContext";
import { Tab } from "../types/Tab";
import PageContainer, { GapSize } from "../components/PageContainer";
import Spinner from "../components/Spinner";
import OrderWrapper from "../components/OrderWrapper";
import Tooltip from "../components/Tooltip";
import {
  fulfillmentLabel,
  fulfillmentBadgeClass,
  fulfillmentDescription,
  hasShipped,
} from "../utils/fulfillmentStatus";

const formatDate = (dateStr: string) => {
  try {
    return new Date(dateStr).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
};

const OrderTrackPage: React.FC = () => {
  const { setDomain, setCategory, setProduct } = useSelectedLocation();
  const { isLoggedIn } = useCustomerAuth();

  useEffect(() => {
    setDomain(Tab.Orders.toString());
    setCategory(null);
    setProduct(null);
  }, [setDomain, setCategory, setProduct]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const searchOrderId: string | null = searchParams.get("id");
  const [orderId, setOrderId] = useState<string>(
    location?.state?.orderId ?? searchOrderId,
  );
  // The buyer email is required to look up an order (ownership proof) — see
  // get-order.js. Seeded from navigation state / query param when available.
  const [email, setEmail] = useState<string>(
    location?.state?.email ?? searchParams.get("email") ?? "",
  );
  const [order, setOrder] = useState<Order | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const fulfillmentState = order?.fulfillments?.[0]?.state;
  const shipmentDetails = order?.fulfillments?.[0]?.shipmentDetails;

  const NOT_FOUND_MESSAGE =
    "We couldn't find that order. Please check the order ID and the email used to place it.";

  const handleTrackOrder = async (lookupId: string, lookupEmail: string) => {
    setIsProcessing(true);
    setLookupError(null);
    try {
      const getOrderResponse = await getOrder(lookupId, { email: lookupEmail });
      if (getOrderResponse?.data) {
        setOrder(getOrderResponse.data);
      } else {
        // Drop any previously-found order: leaving it rendered puts the
        // not-found message directly above a different order's details.
        setOrder(null);
        setLookupError(NOT_FOUND_MESSAGE);
      }
      setIsProcessing(false);
    } catch (error) {
      console.error("Get Order failed:", error);
      setOrder(null);
      setLookupError(NOT_FOUND_MESSAGE);
      setIsProcessing(false);
    }
  };

  // Auto-look-up runs ONCE, and only for values that arrived pre-filled — from
  // navigation state or the query string (e.g. a confirmation email link that
  // carries both id and email). It must NOT depend on the live `orderId` /
  // `email` state: those are the same values the inputs write to, so an effect
  // keyed on them fires a doomed lookup on every keystroke while someone types
  // their email. Everything else searches on submit.
  const seeded = useRef({ orderId, email });
  const didAutoLookup = useRef(false);
  useEffect(() => {
    const { orderId: seedId, email: seedEmail } = seeded.current;
    if (!didAutoLookup.current && !!seedId && !!seedEmail) {
      didAutoLookup.current = true;
      handleTrackOrder(seedId, seedEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const timelineMilestones = [
    { label: "Order Placed", value: shipmentDetails?.placedAt },
    { label: "In Progress", value: shipmentDetails?.inProgressAt },
    { label: "Packaged", value: shipmentDetails?.packagedAt },
    { label: "Expected Shipment", value: shipmentDetails?.expectedShippedAt },
    { label: "Shipped", value: shipmentDetails?.shippedAt },
    { label: "Canceled", value: shipmentDetails?.canceledAt },
    { label: "Failed", value: shipmentDetails?.failedAt },
  ].filter((m) => !!m.value);

  // Only show Return once the order has actually shipped — read from the
  // FULFILLMENT, not order.state, which the printful-webhook leaves OPEN.
  // A shipped order that was later canceled (the refund path state-cancels it)
  // is already refunded — don't offer a return on it. The account order page
  // gates its whole action row the same way.
  const canReturn =
    hasShipped(order ?? undefined) && order?.state !== "CANCELED";
  // Only show Cancel when the order hasn't already been canceled or shipped.
  // Cancel on a shipped order was never actionable — cancelAll's Printful
  // pre-flight rejects it with "already being made" — it just offered the
  // customer a dead end alongside Return.
  const canCancel = !!order && order.state !== "CANCELED" && !canReturn;

  return (
    <>
      {isProcessing && <Spinner text="Looking Up Order..." />}

      <PageContainer hasPadding gapSize={GapSize.Tiny}>
        <OrderWrapper>
          {/* Header */}
          <div className="order-page-header">
            <h1 className="order-page-title">Track Your Order</h1>
            <p className="order-page-subtitle">
              Enter your order ID to view the current status and shipment
              details.
            </p>
          </div>

          {/* The lookup only ever runs on submit — button click or Enter in
              either field — never while the customer is still typing. */}
          <form
            className="flex flex-col gap-4 w-full"
            onSubmit={(e) => {
              e.preventDefault();
              handleTrackOrder(orderId ?? "", email ?? "");
            }}
          >
            {/* Order ID Lookup */}
            <div className="order-input-group">
              <label htmlFor="trackOrderId" className="order-input-label">
                Order ID
              </label>
              <input
                id="trackOrderId"
                type="text"
                name="orderId"
                placeholder="e.g. YzSh5Q3n5RnVpQ7JXTzK0mH8VZfZY"
                value={orderId || ""}
                onChange={(e) => {
                  setOrderId(e.target.value);
                  setLookupError(null);
                }}
                className="order-input-field max-w-lg"
                required
              />
            </div>

            {/* Email — required to prove the order belongs to the requester */}
            <div className="order-input-group">
              <label htmlFor="trackOrderEmail" className="order-input-label">
                Email used on the order
              </label>
              <input
                id="trackOrderEmail"
                type="email"
                name="email"
                placeholder="you@example.com"
                value={email || ""}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setLookupError(null);
                }}
                className="order-input-field max-w-lg"
                required
              />
            </div>

            {!!lookupError && (
              <p
                className="lora-text text-sm max-w-lg text-red-800"
                role="alert"
              >
                {lookupError}
              </p>
            )}

            <button
              type="submit"
              className="order-action-btn order-action-btn--primary w-full max-w-xs"
              disabled={isProcessing}
            >
              Track Order
            </button>
          </form>

          {/* Bridge to the account page: this lookup needs an order ID every
              time, while a signed-in customer gets their orders listed. */}
          <p className="text-sm text-gray-500 lora-text">
            {isLoggedIn ? (
              <>
                You can see your order history any time in{" "}
                <Link
                  to="/account"
                  className="font-semibold text-brown-dark underline underline-offset-2 hover:text-saddle"
                >
                  your account
                </Link>
                .
              </>
            ) : (
              <>
                Have an account?{" "}
                <Link
                  to="/account/login"
                  className="font-semibold text-brown-dark underline underline-offset-2 hover:text-saddle"
                >
                  Sign in
                </Link>{" "}
                to see your order history — no order ID needed.
              </>
            )}
          </p>

          {/* Results */}
          {!!order && (
            <>
              <div className="order-divider" />

              {/* Fulfillment Status */}
              <div className="flex flex-col gap-2">
                <h2 className="order-section-title">Fulfillment Status</h2>
                <Tooltip
                  tooltip={fulfillmentDescription(fulfillmentState)}
                  position="right"
                  multiline
                >
                  <span
                    className={`${fulfillmentBadgeClass(
                      fulfillmentState,
                    )} cursor-help`}
                  >
                    {fulfillmentLabel(fulfillmentState)}
                  </span>
                </Tooltip>
              </div>

              {/* Shipping Info */}
              {(shipmentDetails?.carrier ||
                shipmentDetails?.trackingNumber ||
                shipmentDetails?.shippingType ||
                shipmentDetails?.trackingUrl ||
                shipmentDetails?.shippingNote) && (
                <>
                  <div className="order-divider" />
                  <div className="flex flex-col gap-3">
                    <h2 className="order-section-title">
                      Shipping Information
                    </h2>
                    <div className="order-detail-rows">
                      {!!shipmentDetails?.shippingType && (
                        <div className="order-detail-row">
                          <span className="order-detail-label">
                            Shipping Type
                          </span>
                          <span className="order-detail-value">
                            {shipmentDetails.shippingType}
                          </span>
                        </div>
                      )}
                      {!!shipmentDetails?.carrier && (
                        <div className="order-detail-row">
                          <span className="order-detail-label">Carrier</span>
                          <span className="order-detail-value">
                            {shipmentDetails.carrier}
                          </span>
                        </div>
                      )}
                      {!!shipmentDetails?.trackingNumber && (
                        <div className="order-detail-row">
                          <span className="order-detail-label">Tracking #</span>
                          <span
                            className="order-detail-value font-mono text-xs"
                            style={{ letterSpacing: "0.05em" }}
                            translate="no"
                          >
                            {shipmentDetails.trackingNumber}
                          </span>
                        </div>
                      )}
                      {!!shipmentDetails?.trackingUrl && (
                        <div className="order-detail-row">
                          <span className="order-detail-label">
                            Track Package
                          </span>
                          <a
                            href={shipmentDetails.trackingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="order-detail-link"
                          >
                            View Tracking →
                          </a>
                        </div>
                      )}
                      {!!shipmentDetails?.shippingNote && (
                        <div className="order-detail-row">
                          <span className="order-detail-label">Note</span>
                          <span className="order-detail-value">
                            {shipmentDetails.shippingNote}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Shipment Timeline */}
              {timelineMilestones.length > 0 && (
                <>
                  <div className="order-divider" />
                  <div className="flex flex-col gap-3">
                    <h2 className="order-section-title">Shipment Timeline</h2>
                    <div className="order-timeline">
                      {timelineMilestones.map((milestone) => (
                        <div
                          key={milestone.label}
                          className="order-timeline-item"
                        >
                          <span className="order-timeline-label">
                            {milestone.label}
                          </span>
                          <span className="order-timeline-value">
                            {formatDate(milestone.value!)}
                          </span>
                        </div>
                      ))}
                    </div>
                    {!!shipmentDetails?.cancelReason && (
                      <p
                        className="lora-text text-sm"
                        style={{ color: "#991b1b" }}
                      >
                        Cancellation Reason: {shipmentDetails.cancelReason}
                      </p>
                    )}
                    {!!shipmentDetails?.failureReason && (
                      <p
                        className="lora-text text-sm"
                        style={{ color: "#991b1b" }}
                      >
                        Failure Reason: {shipmentDetails.failureReason}
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* Delivery Address */}
              {!!shipmentDetails?.recipient && (
                <>
                  <div className="order-divider" />
                  <div className="flex flex-col gap-3">
                    <h2 className="order-section-title">Delivery Address</h2>
                    <div className="order-recipient-card">
                      {(shipmentDetails.recipient.displayName ||
                        shipmentDetails.recipient.emailAddress ||
                        shipmentDetails.recipient.phoneNumber) && (
                        <div className="flex flex-col gap-0.5 mb-3">
                          {shipmentDetails.recipient.displayName && (
                            <p
                              className="lora-text font-semibold text-sm"
                              style={{ color: "#1a252f" }}
                            >
                              {shipmentDetails.recipient.displayName}
                            </p>
                          )}
                          {shipmentDetails.recipient.emailAddress && (
                            <p
                              className="opensans-text text-sm"
                              style={{ color: "#6b7280" }}
                            >
                              {shipmentDetails.recipient.emailAddress}
                            </p>
                          )}
                          {shipmentDetails.recipient.phoneNumber && (
                            <p
                              className="opensans-text text-sm"
                              style={{ color: "#6b7280" }}
                            >
                              {shipmentDetails.recipient.phoneNumber}
                            </p>
                          )}
                        </div>
                      )}
                      {!!shipmentDetails.recipient.address && (
                        <div
                          className="flex flex-col gap-0.5 lora-text text-sm"
                          style={{ color: "#2c3e50" }}
                        >
                          <div>
                            {shipmentDetails.recipient.address.firstName}{" "}
                            {shipmentDetails.recipient.address.lastName}
                          </div>
                          <div>
                            {shipmentDetails.recipient.address.addressLine1}
                          </div>
                          {shipmentDetails.recipient.address.addressLine2 && (
                            <div>
                              {shipmentDetails.recipient.address.addressLine2}
                            </div>
                          )}
                          {shipmentDetails.recipient.address.addressLine3 && (
                            <div>
                              {shipmentDetails.recipient.address.addressLine3}
                            </div>
                          )}
                          <div>
                            {[
                              shipmentDetails.recipient.address.locality,
                              shipmentDetails.recipient.address
                                .administrativeDistrictLevel1,
                            ]
                              .filter(Boolean)
                              .join(", ")}{" "}
                            {shipmentDetails.recipient.address.postalCode}
                          </div>
                          {shipmentDetails.recipient.address.country && (
                            <div>
                              {shipmentDetails.recipient.address.country}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Actions — a canceled order offers neither, so don't render a
                  bare divider above an empty row. */}
              {(canReturn || canCancel) && (
                <>
                  <div className="order-divider" />
                  <div className="order-action-row">
                    {canReturn && (
                      <button
                        type="button"
                        className="order-action-btn order-action-btn--secondary flex-1"
                        onClick={() =>
                          navigate("/orders/return", {
                            state: { order, email },
                          })
                        }
                      >
                        Return Order
                      </button>
                    )}
                    {canCancel && (
                      <button
                        type="button"
                        className="order-action-btn order-action-btn--danger flex-1"
                        onClick={() =>
                          navigate("/orders/cancel", {
                            state: { orderId, email },
                          })
                        }
                      >
                        Cancel Order
                      </button>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </OrderWrapper>
      </PageContainer>
    </>
  );
};

export default OrderTrackPage;
